import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { 
  Shield, Cpu, Code, Cloud, Activity, Zap,
  ArrowRight, Check, ChevronRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  Shield, Cpu, Code, Cloud, Activity, Zap,
  Microchip: Cpu
};

export default function ServicesPage() {
  const [services, setServices] = useState([]);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data.services);
    } catch (error) {
      console.error('Failed to fetch services:', error);
    }
  };

  return (
    <div data-testid="services-page" className="min-h-screen pt-16">
      {/* Hero */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-[#05050A]">
          <div className="absolute inset-0 grid-bg opacity-30" />
          <div className="absolute top-1/2 left-0 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[150px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <Badge className="mb-6 bg-primary/10 text-primary border-primary/30 font-mono text-xs">
              OUR CAPABILITIES
            </Badge>
            <h1 className="font-heading text-5xl md:text-6xl font-bold tracking-tight mb-6">
              Technology Services
              <span className="block text-primary">That Deliver Results</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl">
              From concept to deployment, we provide end-to-end technology solutions 
              that transform businesses and protect critical infrastructure.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 space-y-24">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon] || Shield;
            const isEven = index % 2 === 0;
            
            return (
              <motion.div
                key={service.id}
                id={service.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                className={`grid lg:grid-cols-2 gap-12 items-center ${!isEven ? 'lg:flex-row-reverse' : ''}`}
                data-testid={`service-section-${service.id}`}
              >
                <div className={isEven ? '' : 'lg:order-2'}>
                  <div className="w-16 h-16 bg-primary/10 border border-primary/30 flex items-center justify-center mb-6">
                    <IconComponent className="w-8 h-8 text-primary" />
                  </div>
                  
                  <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-4">
                    {service.title}
                  </h2>
                  
                  <p className="text-muted-foreground leading-relaxed mb-8">
                    {service.description}
                  </p>

                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-3">
                        <div className="w-5 h-5 bg-primary/20 flex items-center justify-center">
                          <Check className="w-3 h-3 text-primary" />
                        </div>
                        <span className="text-sm text-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Link to="/contact">
                    <Button className="bg-primary text-primary-foreground font-semibold uppercase tracking-wider btn-chamfer">
                      Get Started
                      <ChevronRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>

                <div className={`relative ${isEven ? '' : 'lg:order-1'}`}>
                  <div className="aspect-[4/3] bg-card/30 border border-white/10 relative overflow-hidden">
                    <div className="absolute inset-0 grid-bg opacity-50" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="relative">
                        <IconComponent className="w-24 h-24 text-primary/30" />
                        <div className="absolute inset-0 bg-primary/10 blur-3xl" />
                      </div>
                    </div>
                    {/* Decorative elements */}
                    <div className="absolute top-4 left-4 font-mono text-xs text-muted-foreground">
                      {`// ${service.id.toUpperCase()}`}
                    </div>
                    <div className="absolute bottom-4 right-4 font-mono text-xs text-primary/50">
                      v3.2.1
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-black/30">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="mb-4 bg-secondary/10 text-secondary border-secondary/30 font-mono text-xs">
              OUR PROCESS
            </Badge>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight">
              How We Work
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'Discovery', desc: 'Deep dive into your requirements and infrastructure' },
              { step: '02', title: 'Architecture', desc: 'Design scalable, secure system architecture' },
              { step: '03', title: 'Development', desc: 'Agile implementation with continuous integration' },
              { step: '04', title: 'Deployment', desc: 'Seamless rollout with 24/7 monitoring' },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative p-6 bg-card/30 border border-white/10"
              >
                <div className="font-mono text-5xl font-bold text-primary/20 mb-4">
                  {item.step}
                </div>
                <h3 className="font-heading text-xl font-semibold mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {item.desc}
                </p>
                {index < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-px bg-white/20" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-secondary/5" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-6">
            Let&apos;s Build Something
            <span className="text-primary"> Extraordinary</span>
          </h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            Ready to discuss your project? Our team is here to help you navigate the complexities of modern technology.
          </p>
          <Link to="/contact">
            <Button 
              size="lg"
              className="bg-primary text-primary-foreground font-bold uppercase tracking-wider btn-chamfer glow-cyan h-14 px-8"
              data-testid="services-cta-btn"
            >
              Contact Our Team
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
